import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Search, UserPlus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { formatDate, formatMoney } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", aliases: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/customers");
      setCustomers(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) =>
      [c.name, ...(c.aliases || [])].some((n) => (n || "").toLowerCase().includes(q))
    );
  }, [customers, query]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const aliases = form.aliases.split(",").map((s) => s.trim()).filter(Boolean);
      await api.post("/customers", { name: form.name.trim(), aliases, notes: form.notes });
      toast.success("Kunde angelegt");
      setDialogOpen(false);
      setForm({ name: "", aliases: "", notes: "" });
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const newCount = customers.filter((c) => c.auto_created).length;

  return (
    <div data-testid="customers-page">
      <PageHeader
        title="Kunden"
        subtitle={`${customers.length} Profile${newCount ? ` • ${newCount} automatisch erstellt` : ""}`}
        actions={
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
            data-testid="customer-add-btn"
          >
            <UserPlus size={16} className="mr-2" /> Kunde anlegen
          </Button>
        }
      >
        Alle Kundenprofile inklusive automatisch aus Reports angelegter Datensätze.
      </PageHeader>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8 space-y-4">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Kunden suchen …"
            className="pl-9"
            data-testid="customer-search-input"
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">1. Payment-Provision</th>
                <th className="text-left px-4 py-3 font-semibold">1. Lizenz-Provision</th>
                <th className="text-right px-4 py-3 font-semibold">Provision total</th>
                <th className="text-left px-4 py-3 font-semibold">Quelle</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Lade Kunden …
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                    Keine Kunden gefunden.
                  </td>
                </tr>
              )}
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-slate-100 hover:bg-slate-50"
                  data-testid={`customer-row-${c.id}`}
                >
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={`/customers/${c.id}`}
                      className="text-slate-900 hover:underline underline-offset-2"
                      data-testid={`customer-link-${c.id}`}
                    >
                      {c.name}
                    </Link>
                    {c.aliases?.length > 0 && (
                      <div className="text-xs text-slate-400 mt-0.5">
                        Aliase: {c.aliases.join(", ")}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {formatDate(c.first_payment_provision_date)}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {formatDate(c.first_license_provision_date)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">
                    {formatMoney(c.total_commission_to_date)}
                  </td>
                  <td className="px-4 py-3">
                    {c.auto_created ? (
                      <span className="inline-flex items-center gap-1 text-xs text-blue-700 font-semibold">
                        <Sparkles size={12} /> Auto
                      </span>
                    ) : (
                      <span className="text-xs text-slate-500">Manuell</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="customer-create-dialog">
          <DialogHeader>
            <DialogTitle>Neuen Kunden anlegen</DialogTitle>
            <DialogDescription>
              Sobald der nächste Report hochgeladen wird, prüft das System automatisch, ob dieser
              Kunde enthalten ist.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="cname">Name</Label>
              <Input
                id="cname"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Restaurant XYZ GmbH"
                data-testid="customer-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="caliases">Alternative Schreibweisen (Komma-getrennt)</Label>
              <Input
                id="caliases"
                value={form.aliases}
                onChange={(e) => setForm({ ...form, aliases: e.target.value })}
                placeholder="z. B. Restaurant XYZ, XYZ Restaurant"
                data-testid="customer-aliases-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cnotes">Notizen</Label>
              <Textarea
                id="cnotes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="z. B. Verkauft am 14.03.2026 durch Maximilian"
                data-testid="customer-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || !form.name.trim()}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="customer-create-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
